import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  ArrowRight, FileSearch, ShieldCheck, Sparkles,
  ScanSearch, CheckCircle2, XCircle, FileText, BrainCircuit,
  Video, GaugeCircle, Lock, Quote,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import MarketingLayout from "./MarketingLayout";
import {
  JobMatchVignette, PredictedQuestionsVignette, MockInterviewVignette,
  OfficialInterviewVignette, ReadinessVignette, BriefVignette, ReadinessGauge,
} from "./vignettes";
import HeroDemo from "./HeroDemo";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5, ease: "easeOut" },
};

const Kicker = ({ children }) => (
  <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500 mb-4">{children}</div>
);

/* ---------------- 1. Hero ---------------- */
const Hero = () => (
  <section className="relative overflow-hidden grain-overlay">
    <div className="ambient-bg absolute inset-0 pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
      <motion.div {...fadeUp} className="lg:col-span-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 mb-7">
          <Sparkles className="h-3.5 w-3.5 text-amber-400" />
          <span className="text-[11px] text-zinc-300">AI interview preparation, built from your actual materials</span>
        </div>
        <h1 className="text-5xl sm:text-6xl tracking-tighter font-semibold text-zinc-50 leading-[1.05]">
          Know What They'll Ask.
          <span className="block text-amber-400 mt-2">Practice Until You're Ready.</span>
        </h1>
        <p className="mt-6 text-lg text-zinc-300 leading-relaxed max-w-xl">
          Prepare for your next interview using your <span className="text-zinc-100 font-medium">actual resume</span> and
          the <span className="text-zinc-100 font-medium">actual job description</span>, predicted questions, AI mock
          interviews with replay, and a measurable Interview Readiness Score.
        </p>
        <div className="mt-9 flex flex-wrap items-center gap-4">
          <Link to="/register" data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-7 py-3.5 shadow-[0_10px_30px_-12px_rgba(245,158,11,0.45)] hover:shadow-[0_14px_40px_-12px_rgba(245,158,11,0.6)] transition-[background-color,box-shadow] duration-300">
            Create Free Account <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-5 text-[12px] text-zinc-500">
          Everything is free during beta. No credit card required.
        </div>
      </motion.div>
      <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.15 }} className="lg:col-span-6">
        <HeroDemo />
      </motion.div>
    </div>
  </section>
);

/* ---------------- 1b. Recognition strip ---------------- */
const RecognitionStrip = () => (
  <section data-testid="section-recognition" className="border-t border-zinc-900">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2 sm:gap-3 text-center sm:text-left">
      <div className="flex items-center justify-center sm:justify-start gap-2">
        <span aria-hidden="true" className="text-lg leading-none">🏆</span>
        <span className="text-sm sm:text-[15px] font-semibold text-zinc-100">Built to compete. Evolved to help you compete.</span>
      </div>
      <span className="hidden sm:inline text-zinc-700">·</span>
      <span className="text-xs sm:text-[13px] text-zinc-400">Top Finalist · Emergent Global Builder Competition · 4,300+ entries worldwide</span>
      <Dialog>
        <DialogTrigger asChild>
          <button
            type="button"
            data-testid="recognition-our-story-btn"
            className="text-xs sm:text-[13px] font-semibold text-amber-500 hover:text-amber-400 transition-colors mx-auto sm:mx-0 sm:ml-1"
          >
            Our Story →
          </button>
        </DialogTrigger>
        <DialogContent data-testid="recognition-story-dialog" className="max-w-lg bg-zinc-950 border-zinc-800 text-zinc-200">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <span aria-hidden="true">🏆</span> Our Story
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-zinc-400 leading-relaxed">
            <p>
              Intuiterview, originally developed as OfferReady, was recognized as a Top
              Finalist in Emergent's Global Builder Competition among more than 4,300
              entries worldwide.
            </p>
            <p>
              Now evolved into Intuiterview, the platform continues to evolve as an AI
              Interview Readiness Platform, helping job seekers understand their fit,
              anticipate likely questions, practice with AI interviewers, and know when
              they're ready for the real interview.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  </section>
);

/* ---------------- 2. Product demonstration ---------------- */
const ProductShowcase = () => (
  <section id="product" data-testid="section-product" className="py-24 sm:py-32 border-t border-zinc-900">
    <span id="how-it-works" className="block relative -top-24" aria-hidden="true" />
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <motion.div {...fadeUp} className="max-w-2xl mb-16">
        <Kicker>The product</Kicker>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-zinc-100">
          From "I want this job" to "I'm ready for the interview."
        </h2>
        <p className="mt-4 text-base text-zinc-400 leading-relaxed">
          Your resume and the job you want become a personalized preparation
          workspace: see your match, know the questions, practice on camera,
          and track your readiness until you're prepared.
        </p>
      </motion.div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[JobMatchVignette, PredictedQuestionsVignette, MockInterviewVignette,
          OfficialInterviewVignette, ReadinessVignette, BriefVignette].map((V, i) => (
          <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }} className="h-full"><V /></motion.div>
        ))}
      </div>
      <motion.div {...fadeUp} className="mt-12">
        <Link to="/register" data-testid="showcase-cta"
              className="inline-flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300 font-medium transition-colors">
          Start your journey free <ArrowRight className="h-4 w-4" />
        </Link>
      </motion.div>
    </div>
  </section>
);

/* ---------------- 3. Problem → Solution ---------------- */
const ProblemSolution = () => (
  <section data-testid="section-problem" className="py-24 sm:py-32 border-t border-zinc-900">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
      <motion.div {...fadeUp}>
        <Kicker>The problem</Kicker>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-zinc-100">
          Generic prep gets generic results.
        </h2>
        <p className="mt-5 text-base text-zinc-400 leading-relaxed max-w-lg">
          Question banks don't know your background, your gaps, or the role
          you're chasing. Interviewers do — they read your resume against their
          job description. Your preparation should start from the same two documents.
        </p>
        <ul className="mt-8 space-y-3">
          {[
            "Rehearsing questions you'll never be asked",
            "No idea how your resume reads against the role",
            "Practicing without feedback, repeating the same mistakes",
            "No way to know if you're actually getting better",
          ].map((t) => (
            <li key={t} className="flex gap-3 items-start text-sm text-zinc-400">
              <XCircle className="h-4 w-4 text-zinc-600 mt-0.5 flex-shrink-0" /> {t}
            </li>
          ))}
        </ul>
      </motion.div>
      <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }}
                  className="rounded-2xl border border-amber-500/20 bg-zinc-900/60 p-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500 mb-6">The Intuiterview way</div>
        <ul className="space-y-4">
          {[
            ["Built around you, not generic templates", "Your resume. Your target role. Your opportunity.", FileText],
            ["Know what the interview may demand", "AI identifies likely questions from your specific role.", BrainCircuit],
            ["Practice with purpose", "Every response becomes actionable coaching.", Video],
            ["Improve with every interview", "See your performance strengthen over time.", ScanSearch],
            ["Know when you're ready", "One Readiness Score brings it all together.", GaugeCircle],
          ].map(([t, s, Icon]) => (
            <li key={t} className="flex gap-3.5 items-start">
              <span className="bg-zinc-800/50 rounded-lg p-2 flex-shrink-0"><Icon className="h-4 w-4 text-amber-400" /></span>
              <span className="pt-0.5">
                <span className="block text-sm text-zinc-200 leading-relaxed font-medium">{t}</span>
                <span className="block text-[13px] text-zinc-500 leading-relaxed">{s}</span>
              </span>
            </li>
          ))}
        </ul>
      </motion.div>
    </div>
  </section>
);

/* ---------------- 6. Readiness deep-dive — signature visual ---------------- */
const useCountUp = (active, to, ms = 1400) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf; const t0 = performance.now();
    const step = (t) => {
      const p = Math.min((t - t0) / ms, 1);
      setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, to, ms]);
  return v;
};

const READINESS_DIMS = [["Communication", 80], ["Answer Structure (STAR)", 71], ["Job Fit", 84], ["Confidence", 75], ["Executive Presence", 74]];

const SignatureReadiness = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const score = useCountUp(inView, 78);
  return (
    <div ref={ref} data-testid="signature-readiness"
         className="relative rounded-3xl border border-amber-500/25 bg-gradient-to-b from-zinc-900/90 to-zinc-950 p-7 sm:p-9 shadow-[0_30px_80px_-30px_rgba(245,158,11,0.18)] w-full max-w-md">
      <div className="flex items-center justify-between mb-5">
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Readiness Report</span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[10px] font-semibold text-amber-300">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> Nearly Ready
        </span>
      </div>
      <div className="flex justify-center">
        <ReadinessGauge score={score} size={230} />
      </div>
      <div className="mt-7 space-y-2.5 w-full">
        {READINESS_DIMS.map(([k, v], i) => (
          <div key={k} className="flex items-center gap-3">
            <span className="text-[12px] text-zinc-400 w-44">{k}</span>
            <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-1.5 rounded-full bg-amber-500/80 transition-[width] duration-1000 ease-out"
                   style={{ width: inView ? `${v}%` : "0%", transitionDelay: `${i * 90}ms` }} />
            </div>
            <span className="text-[12px] font-semibold text-zinc-200 tabular-nums w-7 text-right">{v}</span>
          </div>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
        <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.06] px-3 py-2.5">
          <div className="text-[9px] uppercase tracking-[0.18em] text-emerald-400/90 mb-1">Strength</div>
          <div className="text-[12px] text-zinc-200 font-medium">Job Fit · 84</div>
        </div>
        <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.06] px-3 py-2.5">
          <div className="text-[9px] uppercase tracking-[0.18em] text-amber-400/90 mb-1">Focus area</div>
          <div className="text-[12px] text-zinc-200 font-medium">Structure · 71</div>
        </div>
        <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-2.5">
          <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Next step</div>
          <div className="text-[12px] text-zinc-200 font-medium">Practice STAR answers</div>
        </div>
      </div>
      <div className="mt-4 text-[10px] text-zinc-600 text-right">Illustrative example, not real results</div>
    </div>
  );
};

const ReadinessDeepDive = () => (
  <section id="readiness" data-testid="section-readiness" className="py-24 sm:py-32 border-t border-zinc-900">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <motion.div {...fadeUp} className="order-2 lg:order-1 flex justify-center">
        <SignatureReadiness />
      </motion.div>
      <motion.div {...fadeUp} className="order-1 lg:order-2">
        <Kicker>Interview Readiness Score</Kicker>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-zinc-100">
          Don't just practice. Know you're ready.
        </h2>
        <p className="mt-5 text-base text-zinc-400 leading-relaxed max-w-lg">
          Most candidates stop preparing when they run out of time, not when
          they're ready. Intuiterview scores every mock interview across the
          dimensions interviewers actually judge, shows you exactly what to
          improve next, and tracks whether you're genuinely getting better.
        </p>
        <ul className="mt-8 space-y-3">
          {[
            "A single, trackable readiness number per interview",
            "Broken down by communication, structure, job fit, and presence",
            "Evidence-based: every score is tied to what you actually said",
            "Clear next steps after every practice session",
          ].map((t) => (
            <li key={t} className="flex gap-3 items-start text-sm text-zinc-300">
              <CheckCircle2 className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" /> {t}
            </li>
          ))}
        </ul>
      </motion.div>
    </div>
  </section>
);

/* ---------------- 7. Credibility (factual only) ---------------- */
const Credibility = () => (
  <section data-testid="section-credibility" className="py-16 border-t border-zinc-900">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
      {[
        ["Evidence-based reports", "Scoring across communication, structure, confidence, job fit, and presence, grounded in your transcripts.", FileSearch],
        ["Private by default", "Your resume, recordings, and reports stay in your account. Share a report only if you choose to.", Lock],
        ["Transparent AI", "How our AI evaluates you is documented openly in our Trust Center.", ShieldCheck],
      ].map(([t, d, Icon]) => (
        <motion.div key={t} {...fadeUp} className="flex gap-4 items-start">
          <span className="bg-zinc-800/50 rounded-lg p-2.5 flex-shrink-0"><Icon className="h-4 w-4 text-amber-400" /></span>
          <div>
            <div className="text-sm font-semibold text-zinc-100">{t}</div>
            <p className="mt-1.5 text-[13px] text-zinc-500 leading-relaxed">{d}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);

/* ---------------- Testimonials (real early users) ---------------- */
const MORE_TESTIMONIALS = [
  {
    quote:
      "Just tried Intuiterview and honestly, the experience was great! The mock interview felt realistic, and the feedback was really helpful in identifying areas I could improve. I especially liked how the questions were aligned with the job description. Definitely a useful tool for anyone preparing for an interview. Great work!",
    name: "Jomel Andrei Lucas",
    title: "Registered Medical Technologist",
  },
  {
    quote:
      "The mock interviews felt real. This would help a lot of people prepare for their job interviews.",
    name: "Kendrick Cordero",
    title: "Salesperson",
  },
];

const Testimonial = () => (
  <section data-testid="section-testimonial" className="py-24 sm:py-32 border-t border-zinc-900">
    <div className="max-w-4xl mx-auto px-6 lg:px-8">
      <motion.div {...fadeUp} className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-10 sm:p-12 relative overflow-hidden">
        <Quote className="h-8 w-8 text-amber-500/60 mb-6" />
        <blockquote className="text-lg sm:text-xl text-zinc-200 leading-relaxed tracking-tight">
          I used Intuiterview for my first interview practice, and I was surprised that
          some of the questions it predicted actually came up during my real interview.
          The personalized questions and mock interview helped me know what to expect
          and improve my answers. I felt much more prepared and confident going into
          the interview, and I'm happy to say I got the job as a Finance Associate!
        </blockquote>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <div>
            <div className="text-sm font-semibold text-zinc-100">Sharmaine Kho</div>
            <div className="text-[12px] text-zinc-500">Early Intuiterview user</div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/[0.07] px-3 py-1.5 text-[11px] font-medium text-amber-300">
            <CheckCircle2 className="h-3.5 w-3.5" /> Hired as a Finance Associate
          </span>
        </div>
      </motion.div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
        {MORE_TESTIMONIALS.map((t) => (
          <motion.div
            key={t.name}
            {...fadeUp}
            data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`}
            className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-8 relative overflow-hidden flex flex-col"
          >
            <Quote className="h-6 w-6 text-amber-500/60 mb-4" />
            <blockquote className="text-sm sm:text-base text-zinc-200 leading-relaxed tracking-tight flex-1">
              {t.quote}
            </blockquote>
            <div className="mt-6">
              <div className="text-sm font-semibold text-zinc-100">{t.name}</div>
              <div className="text-[12px] text-zinc-500">{t.title}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- 8. Pricing teaser (real plans) ---------------- */
const PLANS = [
  { name: "Free", price: "$0", cadence: "free during beta", tagline: "Start preparing for free.", features: ["Fit Analysis & Resume Improvement", "ATS Score", "Top 5 Predicted Questions", "Job Match Score"], cta: "Get Started Free", highlight: false },
  { name: "Interview Ready Pass", price: "$0", cadence: "free during beta", tagline: "One interview. Fully prepared.", features: ["Unlimited Mock Interviews", "Unlimited AI Coaching", "Full Interview Day Brief", "Final Readiness Score"], cta: "Try Interview Ready", highlight: true },
  { name: "Career Pro", price: "$0", cadence: "free during beta", tagline: "For active job seekers.", features: ["Unlimited Interview Workspaces", "Unlimited Mocks & Coaching", "Progress Tracking", "Executive Interview Practice"], cta: "Try Career Pro", highlight: false },
  { name: "Career Enterprise", price: "Coming Soon", cadence: "indicative from $199/month", tagline: "For universities, recruiters, and organizations.", features: ["Everything in Career Pro", "Team Management", "Analytics Dashboard", "Dedicated Onboarding"], note: "Enterprise features are under development and are not currently available.", cta: "Explore Enterprise", highlight: false },
];

const PricingTeaser = () => (
  <section id="pricing" data-testid="section-pricing" className="py-24 sm:py-32 border-t border-zinc-900">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <motion.div {...fadeUp} className="max-w-2xl mb-16">
        <Kicker>Pricing</Kicker>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-zinc-100">
          Everything You Need. Free During Beta.
        </h2>
        <p className="mt-3 text-sm text-zinc-500">
          Try every Intuiterview plan and feature while we're in beta. No payment required.
        </p>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {PLANS.map((p, i) => (
          <motion.div key={p.name} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.06 }}
                      data-testid={`pricing-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`rounded-2xl p-7 flex flex-col border hover:-translate-y-1 transition-[transform,border-color] duration-300 ${p.highlight ? "border-amber-500/50 bg-zinc-900/80 shadow-[0_20px_60px_-24px_rgba(245,158,11,0.25)]" : "border-zinc-800 bg-zinc-900/30 hover:border-zinc-700"}`}>
            <div className="text-sm font-semibold text-zinc-100">{p.name}</div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-3xl font-semibold text-zinc-50 tracking-tight">{p.price}</span>
              <span className="text-[11px] text-zinc-500">{p.cadence}</span>
            </div>
            <p className="mt-2 text-[12px] text-zinc-500">{p.tagline}</p>
            <ul className="mt-5 space-y-2 flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex gap-2 items-start text-[13px] text-zinc-300">
                  <CheckCircle2 className="h-3.5 w-3.5 text-amber-400 mt-0.5 flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
            {p.note && <p className="mt-3 text-[11px] text-zinc-600 leading-relaxed">{p.note}</p>}
            <Link to="/pricing" data-testid={`pricing-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`mt-7 inline-flex justify-center rounded-full text-[13px] font-semibold px-5 py-2.5 transition-colors ${p.highlight ? "bg-amber-500 hover:bg-amber-400 text-zinc-950" : "bg-zinc-900 hover:bg-zinc-800 text-zinc-100 border border-zinc-800"}`}>
              {p.cta}
            </Link>
          </motion.div>
        ))}
      </div>
      <motion.div {...fadeUp} className="mt-8 text-center space-y-3">
        <p data-testid="pricing-beta-note" className="text-sm text-zinc-400">
          Intuiterview is currently in beta. All plans are free during the beta period.
        </p>
        <Link to="/pricing" data-testid="pricing-full-link" className="inline-block text-sm text-zinc-400 hover:text-zinc-100 underline underline-offset-4 transition-colors">
          See full pricing details
        </Link>
      </motion.div>
    </div>
  </section>
);

/* ---------------- 9. FAQ ---------------- */
const FAQS = [
  ["What exactly does Intuiterview do?", (
    <>
      <p>Intuiterview is an AI-powered interview preparation platform that helps you stand out, prepare, practice, and approach job interviews with confidence.</p>
      <p className="mt-3">Using your resume and a specific job description, Intuiterview analyzes your job fit, helps improve your resume and cover letter, predicts likely interview questions, and helps you prepare effective answers.</p>
      <p className="mt-3">You can practice through personalized AI mock interviews, experience a realistic Official AI Interview, and receive an Interview Readiness Score with actionable feedback. Before your actual interview, you can also generate a personalized Interview Day Brief to help you prepare.</p>
      <p className="mt-3">From application to interview readiness, Intuiterview helps you prepare for every step of the journey.</p>
    </>
  )],
  ["Is it really free to start?", "Yes. Intuiterview is currently in beta and all plans are free during the beta period - no credit card required. The Free plan includes resume fit analysis and improvement, an ATS score, your top predicted questions, and an Interview Readiness estimate."],
  ["How are the interview questions personalized?", "Questions are generated from your actual resume and the actual job description, the same two documents your interviewer will use, so you practice what you're likely to be asked, not a generic question bank."],
  ["How do AI mock interviews work?", "You answer questions on camera, just like a real video interview. Intuiterview transcribes each answer, evaluates it, asks realistic follow-ups, and produces an executive-style report with replay and evidence-based feedback."],
  ["What is the Official AI Interview?", (
    <>
      <p>The Official AI Interview is Intuiterview's realistic interview simulation. You'll meet a lifelike AI interviewer who speaks to you using voice, asks personalized questions based on your resume and target job, listens to your responses, and can ask relevant follow-up questions.</p>
      <p className="mt-3">You choose the interview experience that best matches the role you're preparing for:</p>
      <ul className="mt-2 space-y-1 list-disc pl-5">
        <li>HR Officer / HR Manager</li>
        <li>Line Manager</li>
        <li>Senior Director / C-Level Executive</li>
        <li>Executive Recruiter / Headhunter</li>
        <li>Technical Lead / Engineering Manager</li>
        <li>Panel Interview</li>
      </ul>
      <p className="mt-3">Each interviewer brings a different perspective and questioning style. The Panel Interview goes a step further by simulating multiple interviewers with different perspectives, especially useful if you're preparing for a panel or multi-interviewer interview.</p>
      <p className="mt-3">The Official AI Interview is designed to feel like a real interview, not simply a conversation with an AI.</p>
    </>
  )],
  ["How is the Official AI Interview different from the AI Mock Interview?", (
    <>
      <p>The AI Mock Interview is designed for practice and improvement, practice questions, strengthen your answers, and build confidence.</p>
      <p className="mt-3">The Official AI Interview is designed to simulate the real interview experience. You choose who interviews you, including a panel, then face personalized questions and realistic follow-ups in a more formal interview setting. Your performance is evaluated to help you understand how ready you are for the actual interview.</p>
      <p className="mt-3"><span className="text-zinc-200 font-medium">AI Mock Interview:</span> Practice. Improve. Build confidence.</p>
      <p className="mt-1"><span className="text-zinc-200 font-medium">Official AI Interview:</span> Choose who interviews you. Experience the interview. Know your readiness.</p>
    </>
  )],
  ["What is the Interview Readiness Score?", "A single measurable score for how prepared you are for a specific interview, broken down by communication, answer structure, job fit, confidence, and presence, so you know exactly what to improve and when you're ready. Scoring is based on the transcript of your answers plus speech metrics like pace and filler words — Intuiterview does not analyze facial expressions, eye contact, appearance, or emotions; video is for simulation and replay only."],
  ["Can I use Intuiterview for the actual job I'm applying for?", "Yes, that's exactly what Intuiterview is built for. Instead of relying only on generic interview questions, you upload your actual resume and paste the actual job description you're applying to. Your alignment analysis, predicted questions, mock interviews, and readiness scoring are all personalized to that specific role."],
  ["Is my resume and video data private?", "Yes. Your resume, recordings, and reports are private to your account. Reports are only visible to others if you explicitly share one — and shared reports never include your raw video. You can permanently delete your recordings and interview history anytime from Settings → Privacy. Details are in our Trust Center."],
];

const FAQ = () => (
  <section id="faq" data-testid="section-faq" className="py-24 sm:py-32 border-t border-zinc-900">
    <div className="max-w-3xl mx-auto px-6 lg:px-8">
      <motion.div {...fadeUp} className="mb-12">
        <Kicker>FAQ</Kicker>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-zinc-100">Common questions</h2>
      </motion.div>
      <motion.div {...fadeUp}>
        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-5 data-[state=open]:border-zinc-700">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-sm text-zinc-100 hover:no-underline py-4">
                {q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-zinc-400 leading-relaxed pb-5">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </motion.div>
    </div>
  </section>
);

/* ---------------- 10. Final CTA ---------------- */
const FinalCTA = () => (
  <section data-testid="section-final-cta" className="py-24 sm:py-32 border-t border-zinc-900 relative overflow-hidden grain-overlay">
    <div className="ambient-bg absolute inset-0 pointer-events-none" />
    <motion.div {...fadeUp} className="relative max-w-3xl mx-auto px-6 text-center">
      <h2 className="text-4xl sm:text-5xl tracking-tighter font-semibold text-zinc-50 leading-tight">
        Your next interview is coming.
        <span className="block text-amber-400 mt-1">Walk in ready.</span>
      </h2>
      <p className="mt-5 text-base text-zinc-400">
        Upload your resume, paste the job description, and see your readiness in minutes.
      </p>
      <div className="mt-9">
        <Link to="/register" data-testid="final-cta"
              className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-8 py-4 shadow-[0_10px_30px_-12px_rgba(245,158,11,0.45)] hover:shadow-[0_14px_40px_-12px_rgba(245,158,11,0.6)] transition-[background-color,box-shadow] duration-300">
          Start Preparing Free <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="mt-4 text-[12px] text-zinc-500">Free to start · No credit card required</div>
    </motion.div>
  </section>
);

export default function MarketingHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user) navigate("/app/dashboard", { replace: true });
  }, [user, navigate]);
  useEffect(() => {
    document.title = "Intuiterview: Know What They'll Ask. Practice Until You're Ready.";
  }, []);

  return (
    <MarketingLayout>
      <div data-testid="marketing-home">
        <Hero />
        <RecognitionStrip />
        <ProductShowcase />
        <ProblemSolution />
        <ReadinessDeepDive />
        <Credibility />
        <Testimonial />
        <PricingTeaser />
        <FAQ />
        <FinalCTA />
      </div>
    </MarketingLayout>
  );
}
